import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { fCodeHooks, locationHooks, meHooks } from '../../hooks/useMasters.js';
import Card from '../../components/ui/Card.jsx';
import Button from '../../components/ui/Button.jsx';
import Input from '../../components/ui/Input.jsx';
import Select from '../../components/ui/Select.jsx';
import Modal from '../../components/ui/Modal.jsx';

export default function FCodesPage() {
  const { data: fcodes = [], isLoading } = fCodeHooks.useList({ includeInactive: 'true' });
  const { data: locations = [] } = locationHooks.useList();
  const { data: mes = [] } = meHooks.useList();
  const create = fCodeHooks.useCreate();
  const update = fCodeHooks.useUpdate();
  const del = fCodeHooks.useDelete();

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const { register, handleSubmit, reset, watch, formState: { errors, isSubmitting } } = useForm();
  const linkedTo = watch('linkedTo');

  const openCreate = () => { setEditing(null); reset({ linkedTo: 'location' }); setModalOpen(true); };
  const openEdit = (f) => {
    setEditing(f);
    reset({
      name: f.name,
      value: f.value,
      description: f.description,
      linkedTo: f.locationId ? 'location' : f.merchantExporterId ? 'merchant' : 'none',
      locationId: f.locationId || '',
      merchantExporterId: f.merchantExporterId || '',
    });
    setModalOpen(true);
  };

  const onSubmit = async (values) => {
    const payload = {
      name: values.name,
      value: parseInt(values.value),
      description: values.description,
      locationId: values.linkedTo === 'location' ? values.locationId : null,
      merchantExporterId: values.linkedTo === 'merchant' ? values.merchantExporterId : null,
    };
    editing
      ? await update.mutateAsync({ id: editing.id, ...payload })
      : await create.mutateAsync(payload);
    setModalOpen(false);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">F Codes</h2>
          <p className="text-sm text-gray-500">Lot number F values — configure per location or merchant exporter</p>
        </div>
        <Button icon={Plus} onClick={openCreate}>Add F Code</Button>
      </div>
      <Card>
        {isLoading ? <div className="p-8 text-center text-gray-400">Loading…</div> : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>{['Code Name', 'F Value', 'Linked To', 'Description', 'Active', ''].map(h => (
                <th key={h} className="text-left px-4 py-3 font-medium text-gray-600 text-xs uppercase">{h}</th>
              ))}</tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {fcodes.map(f => (
                <tr key={f.id} className={`hover:bg-gray-50 ${!f.isActive ? 'opacity-50' : ''}`}>
                  <td className="px-4 py-3 font-mono font-bold">{f.name}</td>
                  <td className="px-4 py-3 font-bold text-teal-700">{f.value}</td>
                  <td className="px-4 py-3 text-gray-600">
                    {f.location ? `📍 ${f.location.code} — ${f.location.name}` :
                     f.merchantExporter ? `🏢 ${f.merchantExporter.name}` : '—'}
                  </td>
                  <td className="px-4 py-3 text-gray-500">{f.description || '—'}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-medium ${f.isActive ? 'text-green-600' : 'text-gray-400'}`}>
                      {f.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right flex gap-2 justify-end">
                    <button onClick={() => openEdit(f)} className="text-gray-400 hover:text-teal-600"><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => del.mutate(f.id)} className="text-gray-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-800">
        <strong>Lot Number Format:</strong> YY-F-XXXX &nbsp;(e.g. 26-1-0001 for F Code value 1 in year 2026)<br/>
        The sequence resets to 0001 every year. Each F value has its own independent sequence.
      </div>

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editing ? 'Edit F Code' : 'Add F Code'}>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Input label="Code Name" required placeholder="e.g. U1CH" {...register('name', { required: true })} error={errors.name && 'Required'} />
            <Input label="F Value (unique integer)" required type="number" placeholder="e.g. 1" {...register('value', { required: true })} error={errors.value && 'Required'} />
          </div>
          <Input label="Description" placeholder="Optional description" {...register('description')} />
          <Select label="Link To" {...register('linkedTo')}>
            <option value="location">Location (Sreeragam)</option>
            <option value="merchant">Merchant Exporter</option>
            <option value="none">None</option>
          </Select>
          {linkedTo === 'location' && (
            <Select label="Location" required {...register('locationId', { required: linkedTo === 'location' })}>
              <option value="">Select location…</option>
              {locations.map(l => <option key={l.id} value={l.id}>{l.code} — {l.name}</option>)}
            </Select>
          )}
          {linkedTo === 'merchant' && (
            <Select label="Merchant Exporter" required {...register('merchantExporterId', { required: linkedTo === 'merchant' })}>
              <option value="">Select merchant exporter…</option>
              {mes.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
            </Select>
          )}
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="secondary" type="button" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button type="submit" loading={isSubmitting}>Save</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
